import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HardHat, Loader2, ArrowRight, Lock, ShieldPlus, Wrench, Truck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { errMessage } from "@/lib/api";
import { SiteFooter } from "@/components/SiteFooter";

export const authInputCls = "mt-1.5 h-12 text-base lg:border-slate-300 lg:bg-white lg:text-slate-900 lg:placeholder:text-slate-400";
export const authLinkCls = "font-semibold text-amber-400 hover:underline lg:text-amber-700";

const Logo = ({ light }) => (
  <div className="flex items-center gap-3">
    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-amber-500 text-slate-950"><HardHat className="h-6 w-6" /></div>
    <div>
      <p className={`font-heading text-3xl font-extrabold uppercase leading-none ${light ? "text-slate-50" : ""}`}>Safe<span className="text-amber-400">Start</span></p>
      <p className="font-data text-[11px] uppercase tracking-widest text-muted-foreground">Sécurité au travail</p>
    </div>
  </div>
);

const CAPABILITIES = [
  [ShieldPlus, "Take 5", "Analyse des risques avant chaque tâche, puis autorisation ou arrêt du travail.", "take5"],
  [Wrench, "Contrôle d'équipements", "Engins, nacelles, outillage : points de contrôle, photos, compteurs. Une anomalie critique bloque l'équipement.", "equipment"],
  [Truck, "Contrôle de véhicules", "État général, kilométrage avec photo du compteur, photos à la prise en charge.", "vehicle"],
];

const Capabilities = ({ dark, prefix }) => (
  <div data-testid={`${prefix}login-capabilities`}>
    <p className={`font-data text-xs uppercase tracking-[0.2em] ${dark ? "text-slate-400" : "text-muted-foreground"}`}>Avec Safe Start</p>
    <p className="mt-1 font-heading text-2xl">Trois contrôles, une seule application</p>
    <ul className="mt-4 space-y-4">
      {CAPABILITIES.map(([Icon, title, text, id]) => (
        <li key={id} data-testid={`${prefix}capability-${id}`} className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-400"><Icon className="h-5 w-5" /></div>
          <div>
            <p className="font-semibold">{title}</p>
            <p className={`text-sm ${dark ? "text-slate-300" : "text-muted-foreground"}`}>{text}</p>
          </div>
        </li>
      ))}
    </ul>
  </div>
);

const BrandPanel = () => (
  <aside data-testid="auth-brand-panel" className="dark-scope relative hidden min-h-screen flex-col overflow-hidden bg-[#1E2A36] text-slate-50 lg:flex">
    <div className="hazard-stripe h-3" />
    <div className="px-14 pt-8 xl:px-20"><Logo light /></div>
    <div className="relative z-10 flex flex-1 flex-col justify-center px-14 py-6 xl:px-20">
      <p className="flex items-center gap-2 font-data text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">
        <span className="h-2 w-2 rounded-full bg-amber-400" /> Prévention · Terrain · Confiance
      </p>
      <h2 className="mt-4 max-w-xl font-heading text-5xl font-extrabold uppercase leading-[0.95] tracking-tight xl:text-6xl">
        Le bon réflexe, <span className="text-amber-400">avant</span> le premier geste.
      </h2>
      <p className="mt-4 max-w-md text-base leading-relaxed text-slate-300">Préparez chaque intervention. Identifiez les risques. Décidez en équipe.</p>
      <div className="mt-8 max-w-lg"><Capabilities dark prefix="" /></div>
    </div>
    <div className="pointer-events-none absolute -bottom-64 -right-56 h-[620px] w-[620px] rounded-full border-[70px] border-white/[0.03]" />
    <div className="hazard-stripe h-3" />
  </aside>
);

export const AuthShell = ({ kicker = "Accès sécurisé", title, subtitle, children }) => (
  <div>
  <div className="grain min-h-screen lg:grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:before:hidden">
    <BrandPanel />
    <div className="flex min-h-screen items-center justify-center px-4 py-10 lg:bg-[#F6F4EE] lg:px-12 lg:text-slate-900">
      <div className="relative z-10 w-full max-w-md rise">
        <div className="hazard-stripe mb-6 h-2 rounded-full lg:hidden" />
        <div className="mb-6 lg:hidden"><Logo /></div>
        <div className="rounded-xl border border-border bg-card p-6 lg:border-0 lg:bg-transparent lg:p-0">
          <p className="mb-3 hidden font-data text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 lg:block">{kicker}</p>
          <h1 className="mb-2 font-heading text-2xl font-bold uppercase lg:text-5xl lg:font-extrabold">{title}</h1>
          {subtitle && <p className="mb-8 hidden text-sm text-slate-600 lg:block">{subtitle}</p>}
          {children}
        </div>
        <div className="mt-8 lg:hidden"><Capabilities prefix="mobile-" /></div>
      </div>
    </div>
  </div>
  <SiteFooter className="bg-card" />
  </div>
);

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Bienvenue." subtitle="Connectez-vous à votre espace de travail.">
      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label htmlFor="email" className="lg:font-semibold">Adresse e-mail</Label>
          <Input id="email" data-testid="login-email-input" type="email" autoComplete="email" placeholder="nom@entreprise.fr" className={authInputCls} value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="lg:font-semibold">Mot de passe</Label>
            <Link to="/mot-de-passe-oublie" data-testid="forgot-password-link" className={`text-sm ${authLinkCls}`}>Mot de passe oublié ?</Link>
          </div>
          <Input id="password" data-testid="login-password-input" type="password" autoComplete="current-password" placeholder="Votre mot de passe" className={authInputCls} value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>
        {error && <p data-testid="login-error" role="alert" className="text-sm text-red-300 lg:text-red-700">{error}</p>}
        <Button data-testid="login-submit-button" type="submit" disabled={busy} className="h-12 w-full text-base font-bold">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Se connecter {!busy && <ArrowRight className="ml-2 h-4 w-4" />}
        </Button>
      </form>
      <p className="mt-5 hidden items-center gap-2 text-xs text-slate-500 lg:flex"><Lock className="h-3.5 w-3.5" /> Votre session est protégée et liée à votre espace de travail.</p>
      <p className="mt-5 text-sm text-muted-foreground lg:text-slate-600">
        Pas encore de compte ? <Link data-testid="go-register-link" to="/inscription" className={authLinkCls}>Créer un compte ou un workplace</Link>
      </p>
    </AuthShell>
  );
}
